import {
  computeRms,
  quantizeInputLevel,
} from "@workspace/voice-shared/audio-level";
import type {
  LiveCallMedia,
  LiveCallPlatform,
  LiveCallStream,
} from "./platform";

const OUTPUT_SPEAKING_LEVEL = 2;
const FFT_SIZE = 256;

// The browser platform only ever sees the MediaStreams it produced.
const asMediaStream = (stream: LiveCallStream) => stream as MediaStream;

function openBrowserMedia(): LiveCallMedia {
  // Created inside the click's task so Safari lets them play audio.
  const ctx = new AudioContext();
  const audio = new Audio();
  audio.autoplay = true;
  const inAnalyser = ctx.createAnalyser();
  inAnalyser.fftSize = FFT_SIZE;
  const outAnalyser = ctx.createAnalyser();
  outAnalyser.fftSize = FFT_SIZE;
  const inBuf = new Float32Array(FFT_SIZE);
  const outBuf = new Float32Array(FFT_SIZE);
  let inSource: MediaStreamAudioSourceNode | null = null;
  let outSource: MediaStreamAudioSourceNode | null = null;

  return {
    activate: () =>
      ctx.state === "suspended"
        ? ctx.resume().catch(() => undefined)
        : Promise.resolve(),
    close: () => {
      audio.pause();
      audio.srcObject = null;
      inSource?.disconnect();
      outSource?.disconnect();
      ctx.close().catch(() => undefined);
    },
    levels: () => {
      inAnalyser.getFloatTimeDomainData(inBuf);
      outAnalyser.getFloatTimeDomainData(outBuf);
      const output = quantizeInputLevel(computeRms(outBuf));
      return {
        input: quantizeInputLevel(computeRms(inBuf)),
        output,
        speaking: output >= OUTPUT_SPEAKING_LEVEL,
      };
    },
    meterInput: (stream) => {
      inSource?.disconnect();
      inSource = ctx.createMediaStreamSource(asMediaStream(stream));
      inSource.connect(inAnalyser);
    },
    playRemote: (stream) => {
      audio.srcObject = asMediaStream(stream);
      audio.play().catch(() => undefined);
      outSource?.disconnect();
      outSource = ctx.createMediaStreamSource(asMediaStream(stream));
      outSource.connect(outAnalyser);
    },
  };
}

/** getUserMedia, RTCPeerConnection, an AudioContext and an `<audio>`. */
export function browserLiveCallPlatform(): LiveCallPlatform {
  return {
    acquireMic: (deviceId) =>
      navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      }),
    createPeerConnection: () => new RTCPeerConnection(),
    listInputDevices: async () => {
      const devices =
        (await globalThis.navigator?.mediaDevices?.enumerateDevices()) ?? [];
      return devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({ deviceId: d.deviceId, label: d.label }));
    },
    newId: () => crypto.randomUUID(),
    openMedia: openBrowserMedia,
    tickMs: 60,
    watchInputDevices: (onChange) => {
      const media = globalThis.navigator?.mediaDevices;
      if (!media) {
        return () => undefined;
      }
      media.addEventListener("devicechange", onChange);
      return () => media.removeEventListener("devicechange", onChange);
    },
  };
}
